/**
 * 银河争霸赛 - 比赛首页纯逻辑 helpers
 *
 * 全部为无副作用纯函数，无 Nuxt 依赖，便于 vitest（node 环境）单测。
 */
import type {
  BattleContestDetailVo,
  GoodsVO,
  YinHeExerciseInfoVO,
} from '@core/types/auto/biz';
import type {
  GalaxyContestInfo,
  GalaxyGoods,
  GalaxyRarity,
  GalaxyUserInfo,
} from '@core/types/galaxy';

// 稀有度 → 背景切图 key（组件内拼资源路径）
export function rarityToBgKey(rarity: GalaxyRarity): 'sr' | 'ur' | 'xr' {
  return rarity.toLowerCase() as 'sr' | 'ur' | 'xr';
}

// 稀有度 → 角标文案
export function rarityToBadge(rarity: GalaxyRarity): string {
  return rarity === 'SR' ? 'SR' : `${rarity}限定`;
}

// 谷子是否已获得（决定彩色/置灰）
export function isGoodsOwned(goods: Pick<GalaxyGoods, 'ownedCount'>): boolean {
  return goods.ownedCount >= 1;
}

// 渲染四态：locked=锁定 / makeup=去补做 / done=已完成 / current=本周
export type GalaxyContestStatus = 'locked' | 'makeup' | 'done' | 'current';

// 赛事旅程节点
export interface JourneyNode {
  exerciseId: number;
  name: string;
  status: GalaxyContestStatus;
  /** 是否为本周节点（与完成态独立，用于「本周」标签） */
  isCurrent: boolean;
}

/**
 * 后端节点作答状态（-1=未作答，1=答题中，2=已完成）→ 渲染四态。
 * 已完成优先：本周已完成同样显示打勾样式，仅「本周」标签仍保留；
 * 「锁定」无独立状态值：后端对「本周之后」的场次同样下发 -1，
 * 故锁定由位置（isAfter）派生，而非 status。
 */
export function resolveContestStatus(
  status: number | undefined,
  isCurrent: boolean,
  isAfter = false,
): GalaxyContestStatus {
  // 已完成（含本周已完成）：显示已完成样式
  if (status === 2) return 'done';
  // 本周节点（未完成）：无论作答进度（-1 未开始 / 1 答题中）都高亮为「本周」
  if (isCurrent) return 'current';
  // 本周之后的场次：锁定，不可补做
  if (isAfter) return 'locked';
  // 本周之前未完成（-1 未作答 / 1 答题中）→ 可补做
  if (status === 1 || status === -1) return 'makeup';
  return 'locked'; // 未知值兜底锁定
}

// 赛事旅程节点列表：由后端 journeyList 映射；本周由 currentId 派生，本周之后锁定
export function buildJourneyNodesFromContests(
  contests: YinHeExerciseInfoVO[],
  currentId: number,
): JourneyNode[] {
  // 本周节点下标；找不到（currentIdx=-1）时不做位置锁定，按 status 映射
  const currentIdx = contests.findIndex(
    (c) => (c.exerciseId ?? 0) === currentId,
  );
  return contests.map((c, i) => ({
    exerciseId: c.exerciseId ?? 0,
    name: c.name ?? '',
    status: resolveContestStatus(
      c.status,
      i === currentIdx,
      currentIdx >= 0 && i > currentIdx,
    ),
    isCurrent: i === currentIdx,
  }));
}

// 主参赛按钮态（未交卷=join / 已交卷=report / 比赛结束=ended）
export type MainAction = 'join' | 'report' | 'ended';
export function resolveMainAction(
  hasSubmitted: boolean,
  endTime: number,
): MainAction {
  // 比赛已结束（当前时间 > 比赛结束时间）
  if (Date.now() > endTime * 1000) return 'ended';
  return hasSubmitted ? 'report' : 'join';
}

// 抽谷按钮是否可点
export function canDrawGoods(drawChances: number): boolean {
  return drawChances > 0;
}

/**
 * 是否隐藏所有「去补做」入口：
 * 末场已结束（now > endTime）且无下一课（hasNext=false）。
 * 后端 endTime 语义：无下课表 → 开课时间+30天（即补做窗口关闭，本身即末场结束）；
 * 有下课表 → 下一课表时间（必然在未来）。
 * 因此 now > endTime 即「末场补做窗口已关闭 且 无下课表」，配合 hasNext 双保险防后端兜底逻辑变化。
 */
export function resolveHideMakeUp(
  endTime: number,
  hasNext: boolean,
  now = Date.now() / 1000,
): boolean {
  if (hasNext) return false;
  return now > endTime;
}

// ── 抽谷次数为 0 时的引导弹窗 ───────────────────────────────────────────────

/** 抽谷引导弹窗态：join=引导加入战局 / done=引导每期完赛 */
export type DrawTipVariant = 'join' | 'done';

export interface DrawTipConfig {
  variant: DrawTipVariant;
  /** 第一行文案（加粗黑字） */
  titleLine: string;
  /** 第二行文案（加粗黑字） */
  subLine: string;
  /** 主按钮文案 */
  buttonText: string;
}

/**
 * 抽谷次数为 0 时的引导弹窗配置。
 * mainAction === 'join'（未参赛/已参赛未交卷）→ 引导加入战局；
 * 其余（report 已交卷 / ended 已结束）→ 引导每期完赛。
 */
export function resolveDrawTip(mainAction: MainAction): DrawTipConfig {
  if (mainAction === 'join') {
    return {
      variant: 'join',
      titleLine: '完成本期战局',
      subLine: '即可获得1次专属谷子抽取机会',
      buttonText: '加入本期战局',
    };
  }
  return {
    variant: 'done',
    titleLine: '完成每期战局',
    subLine: '即可获得1次专属谷子抽取机会',
    buttonText: '我知道了',
  };
}

// 抽谷按钮文案
export function drawButtonText(drawChances: number): string {
  return `专属谷子抽取机会 x${drawChances}`;
}

// 规则说明兜底
export function resolveRuleMarkdown(raw: string | undefined | null): string {
  return raw && raw.trim() ? raw : '规则详情待更新';
}

// 访问守卫决策：未登录 或 非履约 且 非老师 → 重定向首页('/')；否则放行(null)
// 老师身份判定：user.accountId 有值（老师/家长等查看学生数据的账号通常没有 exerciseId）
export function resolveGalaxyAccess(opts: {
  isLogin: boolean;
  isFulfillmentUser: boolean;
  isTeacher?: boolean;
}): '/' | null {
  if (!opts.isLogin) return '/';
  if (!opts.isFulfillmentUser && !opts.isTeacher) return '/';
  return null;
}

// ── 接口字段映射 helpers（getBattleContestDetail → contestInfo / userInfo）───────

/**
 * 时间戳 → 顶部起止时间展示文案
 * startTime/endTime 为秒级时间戳
 * 示例：startTime=1747008000, endTime=1747612800 → "5月12日–5月19日"
 */
export function formatTimeRange(startTime: number, endTime: number): string {
  const fmt = (ts: number) => {
    const d = new Date(ts * 1000);
    return `${d.getMonth() + 1}月${d.getDate()}日`;
  };
  return `${fmt(startTime)}–${fmt(endTime)}`;
}

/**
 * 时间戳 → 赛事旅程头部时间文案
 * startTime/endTime 为秒级时间戳
 * 示例：startTime=1747008000, endTime=1747612800 → "26.10.08–26.11.08"
 */
export function formatJourneyTime(startTime: number, endTime: number): string {
  const fmt = (ts: number) => {
    const d = new Date(ts * 1000);
    const yy = String(d.getFullYear()).slice(2);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yy}.${mm}.${dd}`;
  };
  return `${fmt(startTime)}–${fmt(endTime)}`;
}

/**
 * rarityCode（后端标准字段）→ GalaxyRarity
 * 1=普通→SR, 2=罕见→UR, 3=稀有→XR
 * 未知值兜底 SR
 */
export function rarityCodeToRarity(code: number | undefined): GalaxyRarity {
  switch (code) {
    case 3:
      return 'XR';
    case 2:
      return 'UR';
    case 1:
    default:
      return 'SR';
  }
}

/** GoodsVO → GalaxyGoods */
export function mapGoodsVO(vo: GoodsVO): GalaxyGoods {
  return {
    id: vo.id ?? 0,
    name: vo.name ?? '',
    rarity: rarityCodeToRarity(vo.rarityCode),
    level: vo.goodsAnimation ?? 0,
    icon: vo.url ?? '',
    ownedCount: vo.goodsNum ?? 0,
    goodsLevel: vo.goodsLevel ?? 0,
    levelNum: vo.levelNum ?? null,
  };
}

/**
 * continueText → 累计参赛奖励文案
 * > 0: "再累计参赛 X 周，即可额外获得 1 次抽谷机会"
 * -1 或 0 或无值: "本学期已解锁全部累计参赛奖励"
 */
export function resolveCumulativeRewardText(
  continueText: number | undefined | null,
): string {
  if (continueText != null && continueText > 0) {
    return `再累计参赛 ${continueText} 周，即可额外获得 1 次抽谷机会`;
  }
  return '本学期已解锁全部累计参赛奖励';
}

/** BattleContestDetailVo → GalaxyContestInfo */
export function mapToContestInfo(
  detail: BattleContestDetailVo,
): GalaxyContestInfo {
  return {
    id: detail.id ?? 0,
    image: detail.image,
    level: '', // 后端暂未提供，写死空串
    timeRangeText: formatTimeRange(detail.startTime ?? 0, detail.endTime ?? 0),
    journeyTimeText: formatJourneyTime(
      detail.startTime ?? 0,
      detail.endTime ?? 0,
    ),
    endTime: detail.endTime ?? 0,
    hasNext: (detail.hasNext ?? 1) === 1,
    kingkongs: (detail.icons ?? []).map((i) => ({
      icon: i.iconUrl ?? '',
      text: i.text ?? '',
    })),
    ruleMarkdown: detail.description ?? '',
    journeyList: detail.journeyList ?? [],
  };
}

/** BattleContestDetailVo + exerciseId → GalaxyUserInfo */
export function mapToUserInfo(
  detail: BattleContestDetailVo,
  exerciseId?: number,
): GalaxyUserInfo {
  return {
    isFulfillmentUser: exerciseId != null,
    hasSubmitted: detail.userStatus === 3,
    completedWeeks: detail.contestCount ?? 0,
    drawChances: detail.totalCount ?? 0,
    cumulativeRewardText: resolveCumulativeRewardText(detail.continueText),
    goodsList: (detail.goodsList ?? []).map(mapGoodsVO),
  };
}
